"use client";

import { ArrowUpRight, MoveRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Portfolio() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const projects = [{
    number: "01",
    name: "Digitale Präsenz",
    category: "Webdesign & Branding",
    challenge: "Veraltete Systemarchitektur und mangelnde mobile Optimierung.",
    result: "+140%",
    outcome_tags: "Conversion Rate"
  }, {
    number: "02",
    name: "E-Commerce Lösung",
    category: "Shop-Systeme",
    challenge: "Komplexer Checkout-Prozess führte zu hohen Abbruchraten.",
    result: "45k€",
    outcome_tags: "Mehrumsatz / Monat"
  }, {
    number: "03",
    name: "Landingpage Optimierung",
    category: "Performance Marketing",
    challenge: "Hohe Werbekosten bei geringer Lead-Qualität.",
    result: "-60%",
    outcome_tags: "CPL Reduktion"
  }, {
    number: "04",
    name: "Corporate Identity",
    category: "Branding",
    challenge: "Inkonsistente Markenwahrnehmung über verschiedene Kanäle.",
    result: "100%",
    outcome_tags: "Brand Alignment"
  }, {
    number: "05",
    name: "SEO Strategie",
    category: "Sichtbarkeit",
    challenge: "Keine Sichtbarkeit für relevante lokale Suchbegriffe.",
    result: "#1",
    outcome_tags: "Google Ranking"
  }, {
    number: "06",
    name: "Wartung & Support",
    category: "Technik",
    challenge: "Sicherheitslücken und langsame Ladezeiten der Bestandsseite.",
    result: "0.8s",
    outcome_tags: "Ladezeit (LCP)"
  }];
  return <section data-section-id="287" id="projekt-einblicke" className="relative overflow-hidden bg-background py-24 md:py-40">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={headerRef} className={`grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-20 md:mb-28 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="md:col-span-7">
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-primary mb-8">
              02 &mdash; Unsere Arbeiten
            </p>
            <h2 className="font-serif font-default text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-foreground">
              Digitale <span className="italic font-light">Exzellenz</span> für Oldenburg
            </h2>
          </div>
          <div className="md:col-span-5 md:pt-32 lg:pt-40">
            <p className="font-sans text-base md:text-lg leading-relaxed text-muted-foreground max-w-md">
              Jedes Projekt ist eine maßgeschneiderte Lösung, die Ästhetik mit technischer Präzision verbindet. Entdecken Sie eine Auswahl unserer aktuellen Arbeiten, die wir mit Leidenschaft und Fokus auf messbare Ergebnisse umgesetzt haben.
            </p>
          </div>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 transition-all duration-1000 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {projects.map((project, i) => <Link key={project.number} data-index={i} to="#" aria-label={`Projekt ${project.name} ansehen`} className="group relative block overflow-hidden rounded-lg bg-muted/30 border border-border/50 p-8 min-h-[400px] transition-all duration-500 hover:border-primary/30">
              {}
              <div className="absolute top-0 left-0 h-1 w-0 bg-primary transition-all duration-700 group-hover:w-full" />
              
              <div className="relative z-20 flex h-full flex-col">
                <div className="flex items-start justify-between mb-8">
                  <span className="font-mono text-xs tracking-[0.15em] text-muted-foreground">
                    {project.number}
                  </span>
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-border text-foreground transition-all duration-500 group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary">
                    <ArrowUpRight className="h-4 w-4" />
                  </div>
                </div>

                <div className="mb-auto">
                  <div className="inline-block px-2 py-1 rounded-md bg-primary/5 border border-primary/10 mb-4">
                    <p className="font-mono text-[10px] tracking-wider uppercase text-primary">
                      {project.category}
                    </p>
                  </div>
                  <h3 className="font-serif font-default text-3xl leading-tight text-foreground mb-6">
                    {project.name}
                  </h3>
                </div>

                {}
                <div className="space-y-6 transition-all duration-500 md:opacity-0 md:translate-y-4 group-hover:opacity-100 group-hover:translate-y-0">
                  <div>
                    <p className="font-mono text-[10px] uppercase text-muted-foreground mb-2 tracking-widest">Herausforderung</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {project.challenge}
                    </p>
                  </div>
                  
                  <div className="pt-4 border-t border-border/50">
                    <p className="font-mono text-[10px] uppercase text-muted-foreground mb-1 tracking-widest">Ergebnis</p>
                    <div className="flex items-baseline gap-3">
                      <span className="font-serif text-4xl md:text-5xl font-bold text-primary tracking-tighter">
                        {project.result}
                      </span>
                      <span className="text-xs font-medium text-foreground/70 uppercase tracking-wide">
                        {project.outcome_tags}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>)}
        </div>

        <div ref={ctaRef} className={`mt-20 md:mt-28 flex justify-center transition-all duration-1000 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Button asChild variant="link" className="group h-auto p-0 text-foreground hover:no-underline">
            <Link to="#" className="inline-flex items-center gap-3">
              <span className="relative font-serif font-default text-lg md:text-xl tracking-wide">
                Gesamtes Portfolio ansehen
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-foreground transition-all duration-500 ease-out group-hover:w-full" />
              </span>
              <MoveRight className="h-5 w-5 transition-transform duration-500 ease-out group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}